'use client';

import { useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';
import { BaseLineList } from '@/app/components/icons/BaseLineList';
import { ViduSLogo } from '@/app/components/icons/ViduSLogo';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { useGlobalModal } from '@/app/hooks';
import { useRouter } from '@/i18n/routing';
import { leaveActiveEditingLive } from '../utils/editing-leave-live';
import { StreamUserMenu } from './StreamUserMenu';

export const StreamHeader = ({ preserveLiveId = false }: { preserveLiveId?: boolean }) => {
  const t = useTranslations('viduStreamNavigation');
  const router = useRouter();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  const confirmLeave = (action: () => void) => {
    if (!preserveLiveId) {
      action();
      return;
    }

    openConfirmDialog({
      title: t('leaveLiveConfirm.title'),
      content: t('leaveLiveConfirm.content'),
      secondaryButtonText: t('leaveLiveConfirm.cancel'),
      primaryButtonText: t('leaveLiveConfirm.confirm'),
      onPrimaryAction: () => {
        void (async () => {
          try {
            await leaveActiveEditingLive();
          } finally {
            action();
          }
        })();
      },
    });
  };

  const handleLogoClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (preserveLiveId && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      confirmLeave(() => router.push('/vidu-stream'));
    }
  };

  const openMobileMenu = () => window.dispatchEvent(new CustomEvent('vidu-stream:open-mobile-menu'));

  return (
    <header className="fixed inset-x-0 top-0 z-50 h-[72px] border-system-white08 border-b bg-black/85 px-7 py-5 backdrop-blur-[24px] sm:h-14 sm:px-3 sm:py-2">
      <div className="flex h-full items-center justify-between">
        <ClientLink
          href="/vidu-stream"
          aria-label="Vidu S"
          className="flex shrink-0 items-center"
          onClick={handleLogoClick}
        >
          <ViduSLogo aria-hidden="true" className="h-8 w-[89px] text-white sm:h-5 sm:w-[56px]" />
        </ClientLink>
        <div className="flex items-center gap-2">
          <button
            aria-label="Open menu"
            className="hidden size-9 items-center justify-center rounded-10 bg-system-white08 text-system-white80 sm:flex"
            onClick={openMobileMenu}
            type="button"
          >
            <BaseLineList className="size-5" />
          </button>
          <StreamUserMenu onBeforeLeave={confirmLeave} />
        </div>
      </div>
    </header>
  );
};
